import * as React from 'react';
import type { FormikErrors, FormikTouched} from 'formik';
import { Field } from 'formik';
import { connect } from 'react-redux';
import { sortBy } from 'lodash';
import { FieldRow } from '../../field-row/FieldRow';
import { FieldValidation } from '../../field-validation/FieldValidation';
import type { RootState } from '../../../app/store';
import { PupilFormType } from '../../../containers/pupil-details/PupilDetails';
import { YesNoOptions } from '../../../redux/user-selection/userSelection';
import type { ThirdPartyValues } from '../../../components/customer-details-form/CustomerDetailsForm';
interface InjectedProps {
    counties: endpoints.County[];
}

interface OwnProps {
    formType?: PupilFormType;
    values: ThirdPartyValues;
    errors: FormikErrors<ThirdPartyValues>;
    touched: FormikTouched<ThirdPartyValues>;
    // tslint:disable-next-line:no-any
    setFieldRef?: (field: string, node: any) => void;
    // tslint:disable-next-line:no-any
    setFieldValue: (field: string, value: any) => void;
    sameDetails?: YesNoOptions;
}

export type Props = OwnProps & InjectedProps;

export class ThirdPartyFields extends React.PureComponent<Props> {
    render() {
        const { touched, sameDetails, errors, setFieldRef, counties, formType } = this.props;

        if (formType === PupilFormType.Pupil && (sameDetails === YesNoOptions.Yes || sameDetails === undefined)) {
            return null;
        } else {
            return (
                <div data-testid="third-party-fields">
                    <FieldRow
                        touched={touched.firstName}
                        error={!!errors.firstName}
                        ref={node => setFieldRef && setFieldRef('thirdParty.firstName', node)}
                    >
                        <label className="a-label" htmlFor="thirdParty.firstName" data-testid="thirdparty-firstname">First Name</label>
                        <FieldValidation
                            touched={touched.firstName}
                            errMessage={errors.firstName}
                        />
                        <Field
                            name="thirdParty.firstName"
                            className="a-textbox"
                            placeholder="Enter first name here"
                        />
                    </FieldRow>
                    <FieldRow
                        touched={touched.surname}
                        error={!!errors.surname}
                        ref={node => setFieldRef && setFieldRef('thirdParty.surname', node)}
                    >
                        <label className="a-label" htmlFor="thirdParty.surname" data-testid="thirdparty-surname">Surname</label>
                        <FieldValidation
                            touched={touched.surname}
                            errMessage={errors.surname}
                        />
                        <Field
                            name="thirdParty.surname"
                            className="a-textbox"
                            placeholder="Enter surname here"
                        />
                    </FieldRow>
                    <div className="address-fields">
                        <FieldRow
                            touched={touched.addressLine1}
                            error={!!errors.addressLine1}
                            ref={node => setFieldRef && setFieldRef('thirdParty.addressLine1', node)}
                            className="m-form-row--field-group"
                        >
                            <label className="a-label" htmlFor="addressLine1" data-testid="thirdparty-address1">Billing Address</label>
                            <FieldValidation
                                touched={touched.addressLine1}
                                errMessage={errors.addressLine1}
                            />
                            <Field
                                name="thirdParty.addressLine1"
                                className="a-textbox"
                                placeholder="House name / number"
                            />
                        </FieldRow>
                        <FieldRow
                            touched={touched.addressLine2}
                            error={!!errors.addressLine2}
                            ref={node => setFieldRef && setFieldRef('thirdParty.addressLine2', node)}
                            className="m-form-row--field-group"
                        >
                            <FieldValidation
                                touched={touched.addressLine2}
                                errMessage={errors.addressLine2}
                            />
                            <Field
                                name="thirdParty.addressLine2"
                                className="a-textbox"
                                placeholder="Street name"
                            />
                        </FieldRow>
                        <FieldRow
                            touched={touched.town}
                            error={!!errors.town}
                            ref={node => setFieldRef && setFieldRef('thirdParty.town', node)}
                            className="m-form-row--field-group"
                        >
                            <FieldValidation
                                touched={touched.town}
                                errMessage={errors.town}
                            />
                            <Field
                                name="thirdParty.town"
                                className="a-textbox"
                                placeholder="Town"
                            />
                        </FieldRow>
                        <FieldRow
                            touched={touched.countyId}
                            error={!!errors.countyId}
                            ref={node => setFieldRef && setFieldRef('thirdParty.countyId', node)}
                            className="m-form-row--field-group"
                        >
                            <FieldValidation
                                touched={touched.countyId}
                                errMessage={errors.countyId}
                            />
                            <span className="a-dropdown">
                            <Field
                                component="select"
                                name="thirdParty.countyId"
                                className="a-dropdown__select"
                            >
                                <option value={undefined}>County</option>
                                {sortBy(counties, county => county.name)
                                    .map(county => (
                                        <option value={county.countyId} key={county.countyId}>
                                            {county.name}
                                        </option>
                                    ))}
                            </Field>
                            <span className="a-dropdown__ui"/>
                        </span>
                        </FieldRow>
                    </div>
                    <FieldRow>
                        <label className="a-label" htmlFor="thirdParty.eircode" data-testid="thirdparty-eircode">Eircode (optional)</label>
                        <FieldValidation
                            touched={touched.eircode}
                            errMessage={errors.eircode}
                        />
                        <Field
                            name="thirdParty.eircode"
                            className="a-textbox"
                            placeholder="Enter eircode"
                        />
                    </FieldRow>
                    <FieldRow
                        touched={touched.email}
                        error={!!errors.email}
                        ref={node => setFieldRef && setFieldRef('thirdParty.email', node)}
                    >
                        <label className="a-label" htmlFor="thirdParty.email" data-testid="thirdparty-email">Email address</label>
                        <p>We'll send your payment receipt to this email address.</p>
                        <FieldValidation
                            touched={touched.email}
                            errMessage={errors.email}
                        />
                        <Field
                            type="email"
                            name="thirdParty.email"
                            className="a-textbox"
                            placeholder="Enter email address"
                        />
                    </FieldRow>
                    <FieldRow
                        touched={touched.confirmEmail}
                        error={!!errors.confirmEmail}
                        ref={node => setFieldRef && setFieldRef('thirdParty.confirmEmail', node)}
                    >
                        <label className="a-label" htmlFor="thirdParty.confirmEmail" data-testid="thirdparty-confirmEmail">Reconfirm email address</label>
                        <FieldValidation
                            touched={touched.confirmEmail}
                            errMessage={errors.confirmEmail}
                        />
                        <Field
                            type="email"
                            name="thirdParty.confirmEmail"
                            className="a-textbox"
                            placeholder="Enter email address"
                        />
                    </FieldRow>
                    <FieldRow
                        touched={touched.mobile}
                        error={!!errors.mobile}
                        ref={node => setFieldRef && setFieldRef('thirdParty.mobile', node)}
                    >
                        <label className="a-label" htmlFor="thirdParty.mobile" data-testid="thirdparty-mobile">Mobile phone number</label>
                        <FieldValidation
                            touched={touched.mobile}
                            errMessage={errors.mobile}
                        />
                        <Field
                            type="tel"
                            name="thirdParty.mobile"
                            className="a-textbox"
                            placeholder="Enter cardholder's mobile phone number here"
                        />
                    </FieldRow>
                </div>
            );
        }
    }
}

export function mapStateToProps(state: RootState): InjectedProps {
    return {
        counties: state.counties.countiesAvailable
    };
}

export default connect<InjectedProps, {}, {}, RootState>(mapStateToProps)(ThirdPartyFields);